import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { marketplaceAPI, reviewsAPI } from '../services/api';
import { formatDateTime } from '../utils/dateFormat';
import ReviewForm from '../components/ReviewForm';
import '../styles/Mouvements.css';

const Mouvements = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('sales');
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionId, setActionId] = useState(null);
  const [disputeModal, setDisputeModal] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(null);
  const [reviewedOrders, setReviewedOrders] = useState({});
  const [carriers, setCarriers] = useState([]);
  const [shipModal, setShipModal] = useState(null);
  const [shipCarrier, setShipCarrier] = useState('');
  const [shipTracking, setShipTracking] = useState('');
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  const statusLabels = {
    'pending_payment': t('status.pendingPayment'),
    'paid': t('status.pendingShipment'),
    'shipped': t('status.shippedStatus'),
    'delivered': t('status.delivered'),
    'completed': t('status.completed'),
    'cancelled': t('status.cancelled'),
    'refunded': t('status.refunded'),
    'disputed': t('status.disputed')
  };

  const statusColors = {
    'pending_payment': '#ffc107',
    'paid': '#17a2b8',
    'shipped': '#6f42c1',
    'delivered': '#fd7e14',
    'completed': '#28a745',
    'cancelled': '#6c757d',
    'refunded': '#dc3545',
    'disputed': '#e74c3c'
  };

  useEffect(() => {
    fetchData();
    marketplaceAPI.getCarriers()
      .then((res) => setCarriers(res.data.carriers || []))
      .catch(() => setCarriers([]));
    // Marquer comme vu dès l'ouverture de la page
    marketplaceAPI.markMovementsSeen().catch(() => {});
    // Rafraîchir au retour sur la page (quand elle redevient visible)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        marketplaceAPI.markMovementsSeen().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [salesRes, purchasesRes] = await Promise.all([
        marketplaceAPI.getSalesHistory(),
        marketplaceAPI.getPurchaseHistory()
      ]);
      setSales(salesRes.data.filter(s => s.status !== 'pending_payment'));
      const purchasesList = purchasesRes.data.filter(p => p.status !== 'pending_payment');
      setPurchases(purchasesList);

      // Check which completed orders have reviews
      const completedOrders = purchasesList.filter(p => p.status === 'completed');
      const reviewStatus = {};
      await Promise.all(completedOrders.map(async (order) => {
        try {
          const reviewResponse = await reviewsAPI.canReview(order.id);
          reviewStatus[order.id] = !reviewResponse.data.canReview;
        } catch (e) {
          reviewStatus[order.id] = false;
        }
      }));
      setReviewedOrders(reviewStatus);
    } catch (err) {
      setError(t('movements.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSuccess = () => {
    setShowReviewModal(null);
    setReviewedOrders(prev => ({ ...prev, [showReviewModal.id]: true }));
  };

  const carrierName = (code) => {
    const c = carriers.find((x) => x.code === code);
    return c ? c.name : code;
  };

  // Bloc d'affichage du suivi (vendeur ou acheteur) si un n° est présent.
  const renderTracking = (order) => {
    if (!order.tracking_number && !order.carrier) return null;
    return (
      <div className="mouvement-tracking">
        <div className="tracking-title">{t('movements.tracking', 'Suivi du colis')}</div>
        {order.carrier && (
          <div className="tracking-row">
            <span className="detail-label">{t('movements.carrier', 'Transporteur')}</span>
            <span className="detail-value">{carrierName(order.carrier)}</span>
          </div>
        )}
        {order.tracking_number && (
          <div className="tracking-row">
            <span className="detail-label">{t('movements.trackingNumber', 'N° de suivi')}</span>
            <span className="detail-value">{order.tracking_number}</span>
          </div>
        )}
        {order.tracking_url && (
          <a
            className="tracking-link"
            href={order.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('movements.trackParcel', 'Suivre le colis')}
          </a>
        )}
      </div>
    );
  };

  const openShipModal = (orderId) => {
    setShipModal(orderId);
    setShipCarrier('');
    setShipTracking('');
    setError('');
  };

  const handleConfirmShip = async () => {
    const orderId = shipModal;
    try {
      setActionId(orderId);
      await marketplaceAPI.markAsShipped(orderId, {
        carrier: shipCarrier || undefined,
        tracking_number: shipTracking.trim() || undefined,
      });
      setShipModal(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || t('movements.shipError'));
    } finally {
      setActionId(null);
    }
  };

  const handleConfirmDelivery = async (orderId) => {
    if (!window.confirm(t('confirms.confirmDelivery'))) {
      return;
    }
    try {
      setActionId(orderId);
      await marketplaceAPI.confirmDelivery(orderId);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || t('movements.confirmError'));
    } finally {
      setActionId(null);
    }
  };

  const handleHideOrder = async (orderId) => {
    if (!window.confirm(t('confirms.hideMovement'))) {
      return;
    }
    try {
      await marketplaceAPI.hideOrder(orderId);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleOpenDispute = async (orderId) => {
    if (!disputeReason.trim()) {
      setError(t('movements.describeProblem'));
      return;
    }
    try {
      setActionId(orderId);
      await marketplaceAPI.openDispute(orderId, disputeReason.trim());
      setDisputeModal(null);
      setDisputeReason('');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || t('movements.disputeError'));
    } finally {
      setActionId(null);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/80x80?text=?';
    return `${API_URL.replace('/api', '')}/uploads/${imagePath}`;
  };

  const parseAddress = (addr) => {
    if (!addr) return null;
    try {
      return typeof addr === 'string' ? JSON.parse(addr) : addr;
    } catch (e) {
      return null;
    }
  };

  // Count pending actions
  const pendingSalesCount = sales.filter(s => s.status === 'paid').length;
  const pendingPurchasesCount = purchases.filter(p => p.status === 'shipped').length;

  if (loading) {
    return <div className="mouvements-container"><div className="mouvements-loading">{t('common.loading')}</div></div>;
  }

  return (
    <div className="mouvements-container">
      <h2 className="mouvements-title">{t('movements.title')}</h2>

      {error && <div className="mouvements-error">{error}</div>}

      {/* Tabs */}
      <div className="mouvements-tabs">
        <button
          className={`mouvements-tab ${activeTab === 'sales' ? 'active' : ''}`}
          onClick={() => setActiveTab('sales')}
        >
          {t('movements.sales')}
          {pendingSalesCount > 0 && <span className="tab-badge">{pendingSalesCount}</span>}
        </button>
        <button
          className={`mouvements-tab ${activeTab === 'purchases' ? 'active' : ''}`}
          onClick={() => setActiveTab('purchases')}
        >
          {t('movements.purchases')}
          {pendingPurchasesCount > 0 && <span className="tab-badge">{pendingPurchasesCount}</span>}
        </button>
      </div>

      {/* Onglet Ventes */}
      {activeTab === 'sales' && (
        <div className="mouvements-list">
          {sales.length === 0 ? (
            <div className="mouvements-empty">
              <p>{t('movements.noSales')}</p>
              <button className="btn-primary" onClick={() => navigate('/create-product')}>
                {t('movements.sellItem')}
              </button>
            </div>
          ) : (
            sales.map(sale => {
              const address = parseAddress(sale.shipping_address);
              return (
                <div key={sale.id} className={`mouvement-card ${sale.status === 'paid' ? 'mouvement-action-needed' : ''}`}>
                  <div className="mouvement-header">
                    <img src={getImageUrl(sale.product_image)} alt={sale.product_title} className="mouvement-image" />
                    <div className="mouvement-info">
                      <h4>{sale.product_title}</h4>
                      <p className="mouvement-user">{t('movements.buyerLabel', { name: sale.buyer_name })}</p>
                      <div className="mouvement-meta">
                        <span className="mouvement-amount">+{sale.seller_earnings?.toFixed(2)} CHF</span>
                        <span className="mouvement-status" style={{ backgroundColor: statusColors[sale.status] }}>
                          {statusLabels[sale.status]}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Details livraison */}
                  {sale.status !== 'pending_payment' && (
                    <div className="mouvement-details">
                      <div className="mouvement-detail-row">
                        <span className="detail-label">{t('movements.shippingMode')}</span>
                        <span className="detail-value">
                          {sale.shipping_method === 'shipping' ? t('movements.postalShipping') : t('movements.handDelivery')}
                        </span>
                      </div>

                      {sale.shipping_method === 'shipping' && address && address.name && (
                        <div className="mouvement-detail-row">
                          <span className="detail-label">{t('movements.addressLabel')}</span>
                          <span className="detail-value">
                            {address.name}, {address.street}, {address.postalCode} {address.city}
                          </span>
                        </div>
                      )}

                      {sale.buyer_message && (
                        <div className="mouvement-detail-row">
                          <span className="detail-label">{t('movements.messageLabel')}</span>
                          <span className="detail-value mouvement-message">"{sale.buyer_message}"</span>
                        </div>
                      )}

                      {sale.paid_at && (
                        <div className="mouvement-detail-row">
                          <span className="detail-label">{t('movements.paidOn')}</span>
                          <span className="detail-value">{formatDateTime(sale.paid_at)}</span>
                        </div>
                      )}

                      {sale.shipped_at && (
                        <div className="mouvement-detail-row">
                          <span className="detail-label">{t('movements.shippedOn')}</span>
                          <span className="detail-value">{formatDateTime(sale.shipped_at)}</span>
                        </div>
                      )}

                      {sale.delivered_at && (
                        <div className="mouvement-detail-row">
                          <span className="detail-label">{t('movements.deliveredOn')}</span>
                          <span className="detail-value">{formatDateTime(sale.delivered_at)}</span>
                        </div>
                      )}

                      {renderTracking(sale)}
                    </div>
                  )}

                  {/* Recap paiement pour ventes completees */}
                  {sale.status === 'completed' && (
                    <div className="mouvement-payment-summary">
                      <div className="payment-summary-title">{t('movements.payment')}</div>
                      <div className="payment-summary-rows">
                        <div className="payment-row">
                          <span>{t('movements.itemPrice')}</span>
                          <span>{sale.product_price?.toFixed(2)} CHF</span>
                        </div>
                        {sale.shipping_price > 0 && (
                          <div className="payment-row">
                            <span>{t('movements.shippingCost')}</span>
                            <span>{sale.shipping_price?.toFixed(2)} CHF</span>
                          </div>
                        )}
                        <div className="payment-row total">
                          <span>{t('movements.paidToSeller')}</span>
                          <span>+{sale.seller_earnings?.toFixed(2)} CHF</span>
                        </div>
                      </div>
                      {sale.seller_paid_at && (
                        <div className="payment-date">{t('movements.paidAt', { date: formatDateTime(sale.seller_paid_at) })}</div>
                      )}
                    </div>
                  )}

                  {/* Actions vendeur */}
                  <div className="mouvement-actions">
                    {sale.status === 'paid' && (
                      <button
                        className="btn-ship"
                        onClick={() => openShipModal(sale.id)}
                        disabled={actionId === sale.id}
                      >
                        {actionId === sale.id ? t('common.sending') : t('movements.markShipped')}
                      </button>
                    )}

                    {sale.status === 'shipped' && (
                      <span className="status-badge shipped">{t('movements.waitingConfirmation')}</span>
                    )}

                    {sale.status === 'completed' && (
                      <span className="status-badge completed">{t('movements.saleCompleted')}</span>
                    )}

                    <button
                      className="btn-hide-order"
                      onClick={() => handleHideOrder(sale.id)}
                      title={t('common.delete')}
                    >
                      &times;
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Onglet Achats */}
      {activeTab === 'purchases' && (
        <div className="mouvements-list">
          {purchases.length === 0 ? (
            <div className="mouvements-empty">
              <p>{t('movements.noPurchases')}</p>
              <button className="btn-primary" onClick={() => navigate('/')}>
                {t('movements.discoverItems')}
              </button>
            </div>
          ) : (
            purchases.map(purchase => (
              <div key={purchase.id} className={`mouvement-card ${purchase.status === 'shipped' ? 'mouvement-action-needed' : ''}`}>
                <div className="mouvement-header">
                  <img src={getImageUrl(purchase.product_image)} alt={purchase.product_title} className="mouvement-image" />
                  <div className="mouvement-info">
                    <h4>{purchase.product_title}</h4>
                    <p className="mouvement-user">{t('movements.sellerLabel', { name: purchase.seller_name })}</p>
                    <div className="mouvement-meta">
                      <span className="mouvement-amount purchase">-{purchase.total_amount?.toFixed(2)} CHF</span>
                      <span className="mouvement-status" style={{ backgroundColor: statusColors[purchase.status] }}>
                        {statusLabels[purchase.status]}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Notification expedition */}
                {purchase.status === 'shipped' && (
                  <div className="mouvement-notification">
                    {t('movements.shippedInfo')}
                  </div>
                )}

                {/* Suivi du colis (acheteur) */}
                {['shipped', 'completed', 'disputed'].includes(purchase.status) && renderTracking(purchase)}

                {/* Notification completed */}
                {purchase.status === 'completed' && (
                  <div className="mouvement-notification completed">
                    {t('movements.completedInfo')}
                  </div>
                )}

                {/* Notification litige */}
                {purchase.status === 'disputed' && (
                  <div className="mouvement-notification disputed">
                    {t('movements.disputeInfo')}
                  </div>
                )}

                {/* Details paiement */}
                <div className="mouvement-payment-summary purchase-summary">
                  <div className="payment-summary-title">{t('movements.paymentDetail')}</div>
                  <div className="payment-summary-rows">
                    <div className="payment-row">
                      <span>{t('movements.itemPrice')}</span>
                      <span>{purchase.product_price?.toFixed(2)} CHF</span>
                    </div>
                    {purchase.shipping_price > 0 && (
                      <div className="payment-row">
                        <span>{t('movements.deliveryFee')}</span>
                        <span>{purchase.shipping_price?.toFixed(2)} CHF</span>
                      </div>
                    )}
                    <div className="payment-row">
                      <span>{t('movements.protectionFee')}</span>
                      <span>{purchase.buyer_protection?.toFixed(2)} CHF</span>
                    </div>
                    <div className="payment-row total">
                      <span>{t('movements.totalPaid')}</span>
                      <span>{purchase.total_amount?.toFixed(2)} CHF</span>
                    </div>
                  </div>
                  {purchase.paid_at && (
                    <div className="payment-date">{t('movements.paidOnDate', { date: formatDateTime(purchase.paid_at) })}</div>
                  )}
                  {purchase.delivered_at && (
                    <div className="payment-date">{t('movements.deliveredOnDate', { date: formatDateTime(purchase.delivered_at) })}</div>
                  )}
                </div>

                {/* Actions acheteur */}
                <div className="mouvement-actions">
                  {['paid', 'shipped'].includes(purchase.status) && (
                    <>
                      <button
                        className="btn-confirm-delivery"
                        onClick={() => handleConfirmDelivery(purchase.id)}
                        disabled={actionId === purchase.id}
                      >
                        {actionId === purchase.id ? t('movements.confirming') : t('movements.confirmReceived')}
                      </button>
                      <button
                        className="btn-dispute"
                        onClick={() => { setDisputeModal(purchase.id); setDisputeReason(''); }}
                      >
                        {t('movements.reportProblem')}
                      </button>
                    </>
                  )}

                  {purchase.status === 'disputed' && (
                    <span className="status-badge disputed">{t('status.disputed')}</span>
                  )}

                  {purchase.status === 'completed' && (
                    <>
                      <span className="status-badge completed">{t('movements.transactionCompleted')}</span>
                      {reviewedOrders[purchase.id] === true ? (
                        <span className="status-badge reviewed">{t('movements.reviewSubmitted')}</span>
                      ) : (
                        <button
                          className="btn-leave-review"
                          onClick={() => setShowReviewModal(purchase)}
                        >
                          {t('movements.leaveReview')}
                        </button>
                      )}
                    </>
                  )}

                  <button
                    className="btn-hide-order"
                    onClick={() => handleHideOrder(purchase.id)}
                    title={t('common.delete')}
                  >
                    &times;
                  </button>
                </div>
              </div>
            ))
          )}

          {/* Info protection */}
          {purchases.length > 0 && (
            <div className="mouvements-info-box">
              <strong>{t('movements.protectionTitle')}</strong>
              <p>
                {t('movements.protectionInfo')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Modal litige */}
      {/* Modal expedition (transporteur + n° de suivi) */}
      {shipModal && (
        <div className="dispute-modal-overlay" onClick={() => setShipModal(null)}>
          <div className="dispute-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('movements.shipTitle', 'Confirmer l\'expédition')}</h3>
            <p>{t('movements.shipIntro', 'Renseignez le transporteur et le numéro de suivi (optionnel) pour que l\'acheteur puisse suivre son colis.')}</p>

            <label className="ship-field-label" htmlFor="ship-carrier">
              {t('movements.carrier', 'Transporteur')}
            </label>
            <select
              id="ship-carrier"
              value={shipCarrier}
              onChange={(e) => setShipCarrier(e.target.value)}
            >
              <option value="">{t('movements.carrierPlaceholder', '— Sélectionner (optionnel) —')}</option>
              {carriers.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>

            <label className="ship-field-label" htmlFor="ship-tracking">
              {t('movements.trackingNumber', 'N° de suivi')}
            </label>
            <input
              id="ship-tracking"
              type="text"
              value={shipTracking}
              onChange={(e) => setShipTracking(e.target.value)}
              placeholder={t('movements.trackingPlaceholder', 'Ex : 99.00.123456.78901234')}
            />

            <div className="dispute-modal-actions">
              <button className="btn-cancel" onClick={() => setShipModal(null)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn-submit-dispute"
                onClick={handleConfirmShip}
                disabled={actionId === shipModal}
              >
                {actionId === shipModal ? t('common.sending') : t('movements.confirmShipBtn', 'Confirmer l\'expédition')}
              </button>
            </div>
          </div>
        </div>
      )}

      {disputeModal && (
        <div className="dispute-modal-overlay" onClick={() => setDisputeModal(null)}>
          <div className="dispute-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('movements.disputeTitle')}</h3>
            <p>{t('movements.disputeIntro')}</p>
            <textarea
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              placeholder={t('movements.disputePlaceholder')}
              rows={4}
            />
            <div className="dispute-modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setDisputeModal(null)}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn-submit-dispute"
                onClick={() => handleOpenDispute(disputeModal)}
                disabled={actionId === disputeModal || !disputeReason.trim()}
              >
                {actionId === disputeModal ? t('common.sending') : t('movements.disputeSend')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal avis */}
      {showReviewModal && (
        <div className="review-modal-overlay" onClick={() => setShowReviewModal(null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <ReviewForm
              orderId={showReviewModal.id}
              sellerName={showReviewModal.seller_name}
              onSuccess={handleReviewSuccess}
              onCancel={() => setShowReviewModal(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Mouvements;
